import { useEffect, useState } from "react";
import { Activity, Users } from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";
import { apiRequest } from "../../utils/apiRequest";
import FullPageSpinner from "../../components/FullPageSpinner";

const formatClockTime = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  const now = new Date();

  // strip time for accurate day comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  const diffDays = Math.floor(
    (today.getTime() - targetDay.getTime()) / 86400000,
  );

  const timePart = new Intl.DateTimeFormat("en-GB", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  if (diffDays === 0) {
    return `Today, ${timePart}`;
  }

  if (diffDays === 1) {
    return `Yesterday, ${timePart}`;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

const formatActivityTime = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  if (diffDays === 1) return "Yesterday";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

export default function AdminDashboard() {
  const token = localStorage.getItem("staff_portal_token");

  const [summary, setSummary] = useState({
    total_staff: 0,
    clocked_in: 0,
    hours_this_week: "0h 00m",
    expiring_docs: 0,
  });
  const [clockedInStaff, setClockedInStaff] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);

      const [summaryRes, activityRes] = await Promise.all([
        apiRequest({
          script: "admin-dashboard-summary",
          token,
          data: {},
        }),
        apiRequest({
          script: "admin-get-recent-activity",
          token,
          data: {
            limit: 10,
          },
        }),
      ]);

      if (summaryRes?.success) {
        setSummary(summaryRes.summary || {});
        setClockedInStaff(summaryRes.clocked_in_staff || []);
      }

      if (activityRes?.success) {
        setRecentActivity(activityRes.activities || []);
      }

      setLoading(false);
    };

    fetchDashboardData();
  }, [token]);

  return (
    <AdminLayout title="Dashboard">
      <FullPageSpinner show={loading} />

      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-blue-50 p-6 shadow-sm">
            <p className="text-sm font-medium text-sky-600">Total Staff</p>
            <h3 className="mt-2 text-3xl font-bold text-sky-900">
              {summary.total_staff}
            </h3>
          </div>

          <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 p-6 shadow-sm">
            <p className="text-sm font-medium text-emerald-600">
              Currently Clocked In
            </p>
            <h3 className="mt-2 text-3xl font-bold text-emerald-900">
              {summary.clocked_in}
            </h3>
          </div>

          <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-6 shadow-sm">
            <p className="text-sm font-medium text-violet-600">
              Hours This Week
            </p>
            <h3 className="mt-2 text-3xl font-bold text-violet-900">
              {summary.hours_this_week}
            </h3>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-6 shadow-sm">
            <p className="text-sm font-medium text-amber-600">Expiring Docs</p>
            <h3 className="mt-2 text-3xl font-bold text-amber-900">
              {summary.expiring_docs}
            </h3>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.3fr_0.9fr]">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-slate-50 to-emerald-50 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Live Clocked-In Staff
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Staff currently on shift
                </p>
              </div>

              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 ring-1 ring-emerald-200">
                <Users size={20} className="text-emerald-700" />
              </div>
            </div>

            {clockedInStaff.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-slate-500">
                No staff are currently clocked in.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {clockedInStaff.map((staff, index) => (
                  <div
                    key={staff.attendance_id}
                    className={`flex flex-col gap-4 px-6 py-5 md:flex-row md:items-center md:justify-between ${
                      index % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                    }`}
                  >
                    <div className="min-w-0">
                      {/* mobile inline (below 768px) */}
                      <div className="flex items-center gap-2 md:hidden">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {staff.staff_name}
                        </p>
                        <span className="shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                          {staff.staff_id}
                        </span>
                      </div>

                      {/* tablet & desktop stacked (768px and above) */}
                      <div className="hidden md:block">
                        <p className="text-sm font-semibold text-slate-900">
                          {staff.staff_name}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {staff.staff_id}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 md:w-[460px]">
                      <div className="rounded-xl bg-emerald-50 px-4 py-3 ring-1 ring-emerald-100">
                        <p className="text-xs font-medium text-emerald-600">
                          Clocked In
                        </p>
                        <p className="mt-1 text-sm font-semibold text-emerald-900">
                          {formatClockTime(staff.clock_in)}
                        </p>
                      </div>

                      <div className="rounded-xl bg-sky-50 px-4 py-3 ring-1 ring-sky-100">
                        <p className="text-xs font-medium text-sky-600">
                          Hours Worked
                        </p>
                        <p className="mt-1 text-sm font-semibold text-sky-900">
                          {staff.worked_display}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-slate-50 to-violet-50 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Recent Activity
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Latest actions across the system
                </p>
              </div>

              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-100 ring-1 ring-violet-200">
                <Activity size={20} className="text-violet-700" />
              </div>
            </div>

            {recentActivity.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-slate-500">
                No recent activity yet.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {recentActivity.map((item, index) => (
                  <div
                    key={item.id}
                    className={`px-6 py-4 ${
                      index % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900">
                          {item.description}
                        </p>
                      </div>

                      <p className="whitespace-nowrap text-xs text-slate-500">
                        {formatActivityTime(item.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
